import styled, { css, keyframes } from 'styled-components';
import { SelectOption } from 'src/components/common/Dropdown';
import { useTranslate } from 'react-t7e';
import { FastField, FieldProps } from 'formik';
import { Button } from 'src/components/common/Button';
import { Close } from 'src/components/icons/Close';
import { fromTheme } from 'src/styles/theme';
import { FilterMultiselectDescription } from 'src/components/filters/types';

const FilterMultiselectOptionsContainer = styled.div`
  display: grid;
  justify-items: flex-start;
  grid-auto-flow: row;
  grid-gap: 8px;
`;

const checkAppear = keyframes`
  from {
    transform: rotate(-45deg) scale(0.5);
  }
  to {
    transform: rotate(0deg) scale(1.0);
  }
`;

const checkDisappear = keyframes`
  from {
    transform: rotate(0deg) scale(0.5);
  }
  to {
    transform: rotate(45deg) scale(1.0);
  }
`;

const FilterButton = styled(Button)<{
  $checked: boolean;
  $capitalize?: boolean;
}>`
  && {
    display: block;
    font-style: normal;
    font-weight: normal;
    font-size: 13px;
    text-transform: ${({ $capitalize }) =>
      $capitalize ? 'capitalize' : 'unset'};

    .btn__icon {
      transition: opacity ${fromTheme((t) => t.animation.duration)}s
        ${fromTheme((t) => t.animation.easing.decelerated)};
      ${({ $checked }) =>
        $checked
          ? css`
              opacity: 1;
              animation: ${checkAppear}
                ${fromTheme((t) => t.animation.duration)}s
                ${fromTheme((t) => t.animation.easing.decelerated)} forwards;
            `
          : css`
              opacity: 0;
              animation: ${checkDisappear}
                ${fromTheme((t) => t.animation.duration)}s
                ${fromTheme((t) => t.animation.easing.decelerated)} forwards;
            `}
    }
  }
`;

export const FilterMultiselectOption = (props: {
  name: string;
  option: SelectOption;
}) => {
  const { _ } = useTranslate();
  return (
    <FastField name={props.name}>
      {({ field, form }: FieldProps<string[] | string | undefined>) => {
        const checked = field.value?.includes(props.option.value);
        return (
          <FilterButton
            kind="text"
            icon={Close}
            iconProps={{
              position: 'right',
              size: 10,
            }}
            onClick={() => {
              const currentValue = field.value;
              const anotherValue = props.option.value;

              if (currentValue == null) {
                form.setFieldValue(
                  field.name,
                  anotherValue != null ? [anotherValue] : undefined,
                );
              } else if (typeof currentValue === 'string') {
                form.setFieldValue(
                  field.name,
                  currentValue === anotherValue
                    ? undefined
                    : [currentValue, anotherValue],
                );
              } else if (currentValue.includes(anotherValue)) {
                form.setFieldValue(
                  field.name,
                  currentValue.length === 1 && currentValue[0] === anotherValue
                    ? undefined
                    : currentValue.filter((v) => v !== anotherValue),
                );
              } else {
                form.setFieldValue(field.name, [...currentValue, anotherValue]);
              }
            }}
            $checked={!!checked}
            $capitalize={props.option.translation}
          >
            {props.option.translation
              ? _(props.option.label || '')
              : props.option.label}
          </FilterButton>
        );
      }}
    </FastField>
  );
};

export const FilterMultiselectOptions = ({
  filter,
}: {
  filter: FilterMultiselectDescription;
}) => (
  <FilterMultiselectOptionsContainer>
    {filter.options.map((option) => (
      <FilterMultiselectOption
        key={option.value}
        name={filter.queryField}
        option={option}
      />
    ))}
  </FilterMultiselectOptionsContainer>
);
